import { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import OnboardingGate from "@/components/auth/OnboardingGate";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useMentorRequests } from "@/lib/mentors/useMentorRequests";
import { useMentorProfile } from "@/lib/mentors/useMentorProfile";
import {
  useMentorBookingRequests,
  useMyBookingRequests,
  useCancelBookingRequest,
  useRespondToBooking,
} from "@/lib/mentors/calendar/useMentorCalendar";
import { AvailabilityEditor } from "@/components/mentors/AvailabilityEditor";
import type { MentorMatch, MentorSession } from "@/lib/mentors/types";

const fmtDate = (iso: string | null) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }: { status: string }) => {
  const tone =
    status === "accepted" || status === "scheduled" || status === "completed"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
      : status === "declined" || status === "canceled" || status === "no_show"
        ? "bg-destructive/15 text-destructive"
        : status === "in_progress"
          ? "bg-blue-500/15 text-blue-700 dark:text-blue-300"
          : "bg-amber-500/15 text-amber-700 dark:text-amber-300";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
      {status.replace("_", " ")}
    </span>
  );
};

const MentorSessionsInner = () => {
  const { user } = useAuth();
  const { asMember, asMentor, sessions, loading, respondToRequest } = useMentorRequests();
  const { profile: mentorProfile } = useMentorProfile();
  const isMentor = !!mentorProfile;
  const bookingRequests = useMentorBookingRequests(isMentor ? user?.id ?? null : null);
  const respondBooking = useRespondToBooking();
  const myBookings = useMyBookingRequests(user?.id ?? null);
  const cancelBooking = useCancelBookingRequest();


  const upcoming = useMemo(
    () =>
      sessions
        .filter(
          (s) =>
            s.scheduled_at &&
            new Date(s.scheduled_at).getTime() > Date.now() - 60 * 60 * 1000 &&
            s.status !== "canceled" &&
            s.status !== "completed",
        )
        .sort(
          (a, b) =>
            new Date(a.scheduled_at!).getTime() - new Date(b.scheduled_at!).getTime(),
        ),
    [sessions],
  );

  const past = useMemo(
    () =>
      sessions
        .filter((s) => !upcoming.includes(s))
        .sort((a, b) => {
          const ta = a.scheduled_at ? new Date(a.scheduled_at).getTime() : 0;
          const tb = b.scheduled_at ? new Date(b.scheduled_at).getTime() : 0;
          return tb - ta;
        }),
    [sessions, upcoming],
  );

  const renderRequest = (m: MentorMatch, perspective: "member" | "mentor") => (
    <li
      key={m.id}
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium">
          {perspective === "member" ? "To mentor" : "From member"}:{" "}
          <span className="font-mono text-xs text-muted-foreground">
            {(perspective === "member" ? m.mentor_user_id : m.member_id).slice(0, 8)}
          </span>
        </p>
        {m.message ? (
          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{m.message}</p>
        ) : null}
        <p className="mt-1 text-xs text-muted-foreground">
          {m.app} · {fmtDate(m.created_at)}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <StatusBadge status={m.status} />
        {perspective === "mentor" && m.status === "pending" ? (
          <>
            <button
              type="button"
              onClick={() => respondToRequest(m.id, "accepted")}
              className="rounded-full bg-foreground text-background px-3 py-1 text-xs font-medium"
            >
              Accept
            </button>
            <button
              type="button"
              onClick={() => respondToRequest(m.id, "declined")}
              className="rounded-full border border-border px-3 py-1 text-xs font-medium hover:bg-muted"
            >
              Decline
            </button>
          </>
        ) : null}
      </div>
    </li>
  );

  const renderSession = (s: MentorSession) => {
    const iAmMentor = user?.id === s.mentor_user_id;
    return (
      <li
        key={s.id}
        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
      >
        <div className="min-w-0">
          <p className="text-sm font-medium">
            {iAmMentor ? "Mentoring" : "Session with mentor"} · {s.app}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {fmtDate(s.scheduled_at)}
            {s.duration_minutes ? ` · ${s.duration_minutes} min` : ""}
            {s.price_cents
              ? ` · ${(s.price_cents / 100).toLocaleString(undefined, {
                  style: "currency",
                  currency: (s.currency ?? "USD").toUpperCase(),
                  maximumFractionDigits: 0,
                })}`
              : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={s.status} />
          {s.daily_room_url && (s.status === "scheduled" || s.status === "in_progress") ? (
            <a
              href={s.daily_room_url}
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-foreground text-background px-3 py-1 text-xs font-medium"
            >
              Join call
            </a>
          ) : null}
        </div>
      </li>
    );
  };

  if (loading) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-12 text-sm text-muted-foreground">
        Loading…
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-3xl px-6 py-10">
      <header className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Mentorship
          </p>
          <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">
            My mentor activity
          </h1>
        </div>
        <Link
          to="/mentors"
          className="rounded-full border border-border px-4 py-2 text-xs font-medium hover:bg-muted transition-colors"
        >
          Find a mentor
        </Link>
      </header>

      <section className="mb-10">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Upcoming sessions
        </h2>
        {upcoming.length === 0 ? (
          <p className="text-sm text-muted-foreground">No upcoming sessions.</p>
        ) : (
          <ul className="space-y-2">{upcoming.map(renderSession)}</ul>
        )}
      </section>

      <section className="mb-10">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Requests I've sent
        </h2>
        {asMember.length === 0 ? (
          <p className="text-sm text-muted-foreground">You haven't requested any mentors yet.</p>
        ) : (
          <ul className="space-y-2">{asMember.map((m) => renderRequest(m, "member"))}</ul>
        )}
      </section>

      <section className="mb-10">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          My slot bookings
        </h2>
        {!myBookings.data?.length ? (
          <p className="text-sm text-muted-foreground">
            You haven't requested any time slots yet. Browse{" "}
            <Link to="/mentors" className="underline">mentors</Link> to book one.
          </p>
        ) : (
          <ul className="space-y-2">
            {myBookings.data.map((b) => (
              <li
                key={b.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {new Date(b.proposed_start).toLocaleString()} →{" "}
                    {new Date(b.proposed_end).toLocaleTimeString([], {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </p>
                  {b.member_message ? (
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                      {b.member_message}
                    </p>
                  ) : null}
                  {b.mentor_response ? (
                    <p className="mt-1 text-xs text-muted-foreground italic">
                      Mentor: {b.mentor_response}
                    </p>
                  ) : null}
                  <p className="mt-1 text-xs text-muted-foreground">
                    with <span className="font-mono">{b.mentor_user_id.slice(0, 8)}</span> · {b.app}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={b.status} />
                  {b.status === "pending" ? (
                    <button
                      type="button"
                      onClick={() => cancelBooking.mutate(b.id)}
                      className="rounded-full border border-border px-3 py-1 text-xs font-medium hover:bg-muted"
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mb-10">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Requests I've received
        </h2>
        {asMentor.length === 0 ? (
          <p className="text-sm text-muted-foreground">No incoming requests.</p>
        ) : (
          <ul className="space-y-2">{asMentor.map((m) => renderRequest(m, "mentor"))}</ul>
        )}
      </section>

      {isMentor && user ? (
        <>
          <section className="mb-10">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Slot booking requests
            </h2>
            {bookingRequests.data?.length ? (
              <ul className="space-y-2">
                {bookingRequests.data.map((b) => (
                  <li
                    key={b.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {new Date(b.proposed_start).toLocaleString()} →{" "}
                        {new Date(b.proposed_end).toLocaleTimeString([], {
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </p>
                      {b.member_message ? (
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                          {b.member_message}
                        </p>
                      ) : null}
                      <p className="mt-1 text-xs text-muted-foreground">
                        from <span className="font-mono">{b.member_id.slice(0, 8)}</span> · {b.app}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={b.status} />
                      {b.status === "pending" ? (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              respondBooking.mutate({ id: b.id, action: "confirm" })
                            }
                            className="rounded-full bg-foreground text-background px-3 py-1 text-xs font-medium"
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              respondBooking.mutate({ id: b.id, action: "decline" })
                            }
                            className="rounded-full border border-border px-3 py-1 text-xs font-medium hover:bg-muted"
                          >
                            Decline
                          </button>
                        </>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No slot requests yet.</p>
            )}
          </section>

          <section className="mb-10">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              My availability
            </h2>
            <AvailabilityEditor mentorUserId={user.id} />
          </section>
        </>
      ) : null}

      {past.length > 0 ? (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Past sessions
          </h2>
          <ul className="space-y-2">{past.map(renderSession)}</ul>
        </section>
      ) : null}
    </main>
  );
};

const MentorSessions = () => (
  <div className="min-h-screen flex flex-col bg-background text-foreground">
    <SiteHeader />
    <OnboardingGate>
      <MentorSessionsInner />
    </OnboardingGate>
    <SiteFooter />
  </div>
);

export default MentorSessions;
